/* The trip across S1–S3: a local draft until S3's first search creates the enquiry, the
   enquiry's own `trip` after that. S1/S2/S3 read and write it through `useTrip()`. */
import { useCallback } from 'react';
import { useParams } from 'react-router';
import type { Enquiry, Trip } from '../api/contract';
import { useCreateEnquiry, useEnquiry, useUpdateTrip } from '../api/queries';
import { referralSettled } from './referral';
import { currentEnquiry, tripDraft, updateTripDraft, useSlot, type TripDraft } from './session';

/** The enquiry this screen is about: the `:id` in the URL, else this tab's current check. */
export function useEnquiryId(): string | null {
  const { id } = useParams();
  const current = useSlot(currentEnquiry);
  return id ?? current;
}

/** `useEnquiry(useEnquiryId())`. */
export function useCurrentEnquiry() {
  return useEnquiry(useEnquiryId());
}

/** The resolver's input before the enquiry exists: only the trip is known (→ S1, S2 or S3). */
export function draftEnquiry(
  d: Pick<TripDraft, 'entry' | 'origin' | 'destination' | 'connections' | 'date'>,
): Enquiry {
  const stamp = '1970-01-01T00:00:00Z';
  return {
    id: '00000000000000000000000000',
    claimRef: null,
    passengerLanguage: 'en',
    createdAt: stamp,
    updatedAt: stamp,
    entry: d.entry ?? 'delayed',
    trip: { origin: d.origin, destination: d.destination, connections: d.connections, date: d.date },
    legs: [],
    resultResponse: null,
    quote: null,
    selfReport: null,
    legalAnswer: null,
    contactPrefill: null,
    contact: null,
    eligibility: null,
    eligibilityBasis: 'journey',
    legAlone: null,
    dataDispute: null,
    duplicateOf: null,
    travellingWith: null,
    passengers: [],
    bookingReference: null,
    bookingReferenceSource: null,
    signedAt: null,
    items: [],
    extraNote: null,
    whatsappOptIn: false,
    whatsappPhone: null,
    buddyLink: null,
  };
}

/**
 * The trip as it stands. Once the enquiry has a date, the enquiry's trip is the truth. Before
 * that (no enquiry yet, or one a partial boarding-pass scan created without a date) the per-tab
 * draft is, over whatever the scan read: `updateTrip` needs the date, so route edits wait in
 * the draft until the date comes (`useTrip().save` then sends the whole trip).
 */
export function effectiveTrip(enquiry: Enquiry | undefined, draft: TripDraft): Trip {
  if (enquiry?.trip.date) return enquiry.trip;
  const from = enquiry?.trip;
  const routeInDraft = !!(draft.origin || draft.destination);
  return {
    origin: draft.origin ?? from?.origin ?? null,
    destination: draft.destination ?? from?.destination ?? null,
    connections: routeInDraft ? draft.connections : (from?.connections ?? []),
    date: draft.date ?? from?.date ?? null,
  };
}

export interface TripPatch {
  origin?: Trip['origin'];
  destination?: Trip['destination'];
  connections?: Trip['connections'];
  date?: string | null;
}

/**
 * S1, S2 and S3's summary edits:
 *
 * ```ts
 * const { trip, entry, save, ensureEnquiry } = useTrip();
 * await save({ origin, destination, connections });   // S1
 * await save({ date });                               // S2
 * const id = await ensureEnquiry();                   // S3, before the first search
 * ```
 *
 * `save` writes the draft until the enquiry exists, then `updateTrip` (which resets the answers
 * that depended on the old trip). An enquiry without a date (a partial scan) can't take a route
 * alone: the route waits in the draft, `save` answers null, and the date's `save` sends the
 * whole trip. `trip` is always the effective trip (`effectiveTrip`). `ensureEnquiry` creates
 * the enquiry from the draft (entry, trip, attribution, and the previous check's id to
 * pre-fill S7) the first time, and returns the id.
 */
export function useTrip() {
  const id = useEnquiryId();
  const draft = useSlot(tripDraft);
  const enquiry = useEnquiry(id);
  const create = useCreateEnquiry();
  const update = useUpdateTrip(id);

  const trip: Trip = effectiveTrip(enquiry.data, draft);
  const entry = enquiry.data?.entry ?? draft.entry;

  const { mutateAsync: updateAsync } = update;
  const { mutateAsync: createAsync } = create;
  const current = enquiry.data;

  const save = useCallback(
    async (patch: TripPatch): Promise<Enquiry | null> => {
      updateTripDraft(patch);
      if (!current) return null;
      const next = { ...effectiveTrip(current, tripDraft.get()), ...patch };
      if (!next.origin || !next.destination || !next.date) return null;
      return updateAsync({
        origin: next.origin.iata,
        destination: next.destination.iata,
        connections: next.connections.map((c) => c.iata),
        date: next.date,
      });
    },
    [current, updateAsync],
  );

  const ensureEnquiry = useCallback(async (): Promise<string> => {
    if (id) return id;
    await referralSettled();
    const d = tripDraft.get();
    if (!d.origin || !d.destination || !d.date) throw new Error('ensureEnquiry: the trip is not complete');
    const e = await createAsync({
      entry: d.entry ?? 'delayed',
      trip: {
        origin: d.origin.iata,
        destination: d.destination.iata,
        connections: d.connections.map((c) => c.iata),
        date: d.date,
      },
      ...(Object.keys(d.attribution).length ? { attribution: d.attribution } : {}),
      ...(d.prefillContactFrom ? { prefillContactFrom: d.prefillContactFrom } : {}),
    });
    return e.id;
  }, [id, createAsync]);

  return {
    enquiryId: id,
    trip,
    entry,
    /** The draft's entry (S0 pick) can change before the enquiry exists. */
    setEntry: (e: TripDraft['entry']) => updateTripDraft({ entry: e }),
    save,
    ensureEnquiry,
    isLoading: enquiry.isLoading,
    isSaving: update.isPending || create.isPending,
    error: enquiry.error ?? update.error ?? create.error ?? null,
  };
}
